import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import './index.css';
import { installFlashErrorCapture } from './utils/flashErrorCapture';

// Capture every toast.error / console.error during the app's lifetime
// to `window.__flashErrors` so the user can debug intermittent flashes
// after the toast has already auto-dismissed. Zero-side-effect wrap.
installFlashErrorCapture();

// ─────────────────────────────────────────────────────────────────
// DEV-ONLY: block Vite's "server connection lost → polling → reload"
// auto-refresh. Cloudflare kills idle WebSockets at ~100 s; Vite's
// client (vite/dist/client/client.mjs:562) then calls location.reload()
// DIRECTLY (NOT via the vite:beforeFullReload event), wiping the
// user's session every couple of minutes during normal idle work.
//
// Strategy: Vite always emits "[vite] server connection lost. Polling
// for restart..." just before its reconnect-driven reload. We hook
// console.log to detect that line and set a 90-second window during
// which any window.location.reload() call is silently blocked. After
// the window, normal reloads work again (user-clicked ErrorBoundary
// retry, in-app "Reload" buttons, etc.).
//
// We use the log message rather than a stack-trace check because
// Safari resets stack frames across `await` boundaries — the actual
// reload() call has an empty/native stack so frame-based detection
// never matches.
// ─────────────────────────────────────────────────────────────────
if (import.meta.env.DEV && typeof window !== 'undefined') {
  let reloadBlockedUntil = 0;
  const VITE_LOST_MARKER = '[vite] server connection lost';
  const BLOCK_WINDOW_MS = 90_000; // a bit longer than CF's ~100s timeout

  // 1. Watch console.log for Vite's reconnect message and arm the block.
  try {
    const origLog = console.log.bind(console);
    console.log = function patchedLog(...args: any[]) {
      try {
        const first = args[0];
        if (typeof first === 'string' && first.includes(VITE_LOST_MARKER)) {
          reloadBlockedUntil = Date.now() + BLOCK_WINDOW_MS;
          origLog('[hmr] Vite reconnect detected — auto-reload blocked for 90s');
        }
      } catch {
        /* no-op */
      }
      return origLog(...args);
    };
  } catch {
    /* Console patch unavailable in this runtime — silently skip. */
  }

  // 2. Patch location.reload. Block iff we're inside the Vite-reconnect
  //    window. Outside it, behave normally so user-clicked reloads work.
  //    NOTE: modern Chrome / Safari treat `location.reload` as
  //    non-configurable, so `defineProperty` throws. That's expected —
  //    swallow the failure silently rather than scaring the user with
  //    a red console line on every fresh load. The defenses below
  //    (vite:beforeFullReload throw + dev-only chunk-error guard) are
  //    enough to cover the common reconnect cases on those browsers.
  try {
    const originalReload = window.location.reload.bind(window.location);
    Object.defineProperty(window.location, 'reload', {
      configurable: true,
      writable: true,
      value: function patchedReload(...args: any[]) {
        if (Date.now() < reloadBlockedUntil) {
          console.warn('[hmr] location.reload() blocked — Vite WS reconnect path');
          return;
        }
        return originalReload(...args);
      },
    });
  } catch {
    /* Cannot redefine native property — silently skip. */
  }

  // 3. Belt-and-braces: documented event path for explicit
  //    server-pushed full-reloads.
  if (import.meta.hot) {
    import.meta.hot.on('vite:beforeFullReload', () => {
      // eslint-disable-next-line no-throw-literal
      throw '[hmr] full reload skipped';
    });
  }
}

// Global handler for chunk loading errors.
// In dev, Vite HMR rewrites chunks constantly and the AuthErrorBoundary
// already owns the dev-side recovery (with a 30s cooldown + 2-reload
// cap). Auto-reloading from this top-level handler too would double-fire
// reloads on rate-limit cascades and make the 429 loop WORSE. Stay
// production-only here.
const isProduction = import.meta.env.PROD;

const _maybeReloadAfterChunkError = (reason: string) => {
  console.warn('[App] Chunk load error detected:', reason);
  if (!isProduction) return;
  const now = Date.now();
  const last = parseInt(sessionStorage.getItem('lastChunkErrorReload') || '0', 10);
  // 10s cooldown in prod — stale chunks after a deploy resolve in a
  // single reload, so we don't need a long delay.
  if (last && (now - last) < 10_000) return;
  sessionStorage.setItem('lastChunkErrorReload', now.toString());
  window.location.reload();
};

window.addEventListener('error', (event) => {
  if (
    event.message?.includes('Importing a module script failed') ||
    event.message?.includes('Failed to fetch dynamically imported module') ||
    event.message?.includes('Loading chunk') ||
    event.error?.name === 'ChunkLoadError'
  ) {
    _maybeReloadAfterChunkError(event.message || 'unknown');
  }
});

// Handle unhandled promise rejections (for dynamic imports)
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason?.message || String(event.reason);
  if (
    reason.includes('Importing a module script failed') ||
    reason.includes('Failed to fetch dynamically imported module') ||
    reason.includes('Loading chunk')
  ) {
    _maybeReloadAfterChunkError(reason);
  }
});

// Listen for notification clicks from service worker
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
      console.log('[App] Received notification click from service worker:', event.data.url);
      // Navigate using router instead of window.location to stay in app.
      // Push payloads carry ABSOLUTE urls — strip the origin or the router
      // treats the whole thing as a pathname and the deep link 404s.
      let target = event.data.url;
      try {
        const u = new URL(target, window.location.origin);
        if (u.origin === window.location.origin) {
          target = u.pathname + u.search + u.hash;
        }
      } catch {}
      router.navigate(target);
    }
  });
}

// Listen for foreground notification clicks
window.addEventListener('notification-navigate', ((event: CustomEvent) => {
  console.log('[App] Received notification click event:', event.detail.url);
  // Navigate using router to stay within the app (better for mobile)
  router.navigate(event.detail.url);
}) as EventListener);

// Render the app
import { ThemeProvider } from "utils/ThemeProvider";

// NOTE: React.StrictMode intentionally removed. Its dev-only double
// effect-mounting made every Firestore onSnapshot subscribe→unsubscribe→
// resubscribe within the same tick at login, corrupting the Watch stream's
// pending-response accounting and flooding the console with hundreds of
// "FIRESTORE INTERNAL ASSERTION FAILED: Unexpected state" errors.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <ThemeProvider>
    <RouterProvider router={router} />
  </ThemeProvider>
);
